import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePipeline, { PipelineStepConfig } from '../../utils/hooks/usePipeline';
import { ActionButtonStyled } from '../Modals/ActionButton/styled';
import PipelineStep from './PipelineStep';
import {
    PipelineActionsStyled,
    PipelineContainerStyled,
    PipelineSuccessMessageStyled,
} from './styled';

interface DeploymentPipelineProps {
    steps: PipelineStepConfig[];
    onSuccess?: () => void;
    onSync?: () => Promise<void>;
    launchLabel?: string;
    successLabel?: string;
}

const DeploymentPipeline: FC<DeploymentPipelineProps> = ({ steps, onSuccess, onSync, launchLabel, successLabel }) => {
    const { t } = useTranslation('settings');
    const {
        stepStates, start, retry, hasStarted, isFinished,
    } = usePipeline(steps);
    const [syncStatus, setSyncStatus] = useState<'idle' | 'loading' | 'done'>('idle');

    const handleSync = async () => {
        if (!onSync || syncStatus !== 'idle') { return; }
        setSyncStatus('loading');
        await onSync();
        setSyncStatus('done');
    };

    const syncLabel = () => {
        if (syncStatus === 'loading') { return t('tabs.createSurvey.step6Content.syncing'); }
        if (syncStatus === 'done') { return t('tabs.createSurvey.step6Content.syncSuccess'); }

        return t('tabs.createSurvey.step6Content.syncData');
    };

    return (
        <div>
            <PipelineContainerStyled>
                {steps.map((step) => (
                    <PipelineStep
                        key={step.id}
                        error={stepStates[step.id]?.error}
                        label={step.label}
                        onRetry={() => retry(step.id)}
                        status={stepStates[step.id]?.status ?? 'idle'}
                    />
                ))}
            </PipelineContainerStyled>

            {!hasStarted && (
                <PipelineActionsStyled>
                    <ActionButtonStyled
                        onClick={start}
                        variant="text"
                        default
                        disableRipple
                    >
                        {launchLabel ?? t('tabs.createSurvey.step6Content.launchPipeline')}
                    </ActionButtonStyled>
                </PipelineActionsStyled>
            )}

            {isFinished && (
                <>
                    <PipelineSuccessMessageStyled>
                        {successLabel ?? t('tabs.createSurvey.step6Content.success')}
                    </PipelineSuccessMessageStyled>
                    {onSync && (
                        <PipelineActionsStyled>
                            <ActionButtonStyled
                                disabled={syncStatus !== 'idle'}
                                onClick={handleSync}
                                variant="text"
                                default
                                disableRipple
                            >
                                {syncLabel()}
                            </ActionButtonStyled>
                        </PipelineActionsStyled>
                    )}
                    {onSuccess && (
                        <PipelineActionsStyled>
                            <ActionButtonStyled
                                onClick={onSuccess}
                                variant="text"
                                default
                                disableRipple
                            >
                                {t('tabs.createSurvey.step6Content.close')}
                            </ActionButtonStyled>
                        </PipelineActionsStyled>
                    )}
                </>
            )}
        </div>
    );
};

export default DeploymentPipeline;
